import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { Search, Download, Mail, Building2, Users, ArrowRight, Tag, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Popover, PopoverTrigger, PopoverContent
} from '@/components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
}

function Avatar({ name, email }) {
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : (email || '??').slice(0, 2).toUpperCase();
  const hue = (name || email || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
      style={{ background: `hsl(${hue}, 55%, 55%)` }}>
      {initials}
    </div>
  );
}


function CategoryPicker({ contact, currentCatId, customCategories, onSelect }) {
  const [applyToDomain, setApplyToDomain] = useState(false);
  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 px-1 pb-1">
        Categoria per <strong className="text-gray-700">{contact.email}</strong>
      </div>

      {/* Toggle apply-to-domain */}
      <label
        data-testid="rubrica-toggle-domain"
        className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-50 text-xs"
        style={{ background: applyToDomain ? '#FFF0EC' : 'transparent', border: '1px solid', borderColor: applyToDomain ? '#E2725B33' : '#E8EAED' }}
      >
        <input
          type="checkbox"
          checked={applyToDomain}
          onChange={e => setApplyToDomain(e.target.checked)}
          className="w-3 h-3 accent-orange-600"
        />
        <span className={applyToDomain ? 'text-gray-700 font-medium' : 'text-gray-500'}>
          Applica a tutto il dominio <strong>@{contact.domain}</strong>
        </span>
      </label>

      <div className="h-px bg-gray-100 my-1" />

      {customCategories.map(cat => {
        const isCurrent = cat.id === currentCatId;
        return (
          <button
            key={cat.id}
            onClick={() => onSelect(cat.id, applyToDomain)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-gray-50 transition-all"
          >
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: cat.color }} />
            <span className="flex-1 text-left" style={{ color: cat.color }}>{cat.name}</span>
            {isCurrent && <Check size={12} className="text-orange-600" />}
          </button>
        );
      })}
      <button
        onClick={() => onSelect(null, applyToDomain)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-gray-50 transition-all text-gray-500"
      >
        <X size={12} className="text-gray-400" />
        <span>Nessuna categoria</span>
      </button>
    </div>
  );
}

export default function Rubrica() {
  const navigate = useNavigate();
  const { openCompose, customCategories } = useApp();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [savingKey, setSavingKey] = useState(null); // email del contatto in salvataggio
  const [openPopover, setOpenPopover] = useState(null); // email del contatto con popover aperto

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/rubrica`);
        setContacts(res.data.contacts || []);
      } catch { toast.error('Errore nel caricamento della rubrica'); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.domain?.toLowerCase().includes(q)
    );
  }, [contacts, search]);

  const handleExport = () => {
    setExporting(true);
    try {
      const data = filtered.map(c => ({
        'Nome': c.name || '',
        'Email': c.email || '',
        'Dominio': c.domain || '',
        'Categoria': (c.categories || []).join(', '),
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [{ wch: 25 }, { wch: 35 }, { wch: 22 }, { wch: 25 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rubrica');
      XLSX.writeFile(wb, `rubrica_mariani_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Esportati ${filtered.length} contatti`);
    } catch { toast.error('Errore nell\'esportazione'); }
    finally { setExporting(false); }
  };

  const handleSetCategory = async (contact, categoryId, applyToDomain) => {
    setSavingKey(contact.email);
    try {
      const res = await axios.post(`${API}/rubrica/category`, {
        email: contact.email,
        category: categoryId,
        apply_to_domain: !!applyToDomain,
      });
      const updated = res.data?.updated || 0;
      // Aggiorna stato locale: il contatto corrente prende la nuova lista
      const newCats = categoryId ? [categoryId] : [];
      setContacts(prev => prev.map(c => {
        if (applyToDomain) {
          // tutti i contatti dello stesso dominio
          if ((c.domain || '').toLowerCase() === (contact.domain || '').toLowerCase()) {
            return { ...c, categories: newCats };
          }
          return c;
        }
        return c.email === contact.email ? { ...c, categories: newCats } : c;
      }));
      const catName = customCategories.find(c => c.id === categoryId)?.name;
      if (applyToDomain) {
        toast.success(
          categoryId
            ? `${updated} email del dominio @${contact.domain} categorizzate come "${catName}"`
            : `${updated} email del dominio @${contact.domain}: categoria rimossa`
        );
      } else {
        toast.success(
          categoryId
            ? `${updated} email di ${contact.email} categorizzate come "${catName}"`
            : `${updated} email di ${contact.email}: categoria rimossa`
        );
      }
      setOpenPopover(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento categoria');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: 'hsl(40, 18%, 97%)' }}>
      <div className="max-w-4xl mx-auto px-8 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>Rubrica</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Contatti a cui hai risposto — {contacts.length} totali
            </p>
          </div>
          <button
            data-testid="export-rubrica-btn"
            onClick={handleExport}
            disabled={exporting || filtered.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all hover:shadow-sm"
            style={{ background: '#2DAA7E', opacity: (exporting || filtered.length === 0) ? 0.6 : 1 }}
          >
            <Download size={15} className={exporting ? 'animate-bounce' : ''} />
            {exporting ? 'Esportazione...' : 'Esporta Excel'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { label: 'Contatti rubricati', value: contacts.length, color: '#6B6FD8', bg: '#EDE8FF' },
            { label: 'Risposte totali inviate', value: contacts.reduce((s, c) => s + (c.email_count || 0), 0), color: '#2DAA7E', bg: '#E4F7EE' },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-4 flex items-center gap-3" style={{ background: s.bg }}>
              <div className="text-3xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-sm" style={{ color: s.color, opacity: 0.8 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="rubrica-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome, email o dominio..."
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border focus:outline-none focus:ring-1 transition-all"
            style={{ borderColor: '#D9D2C8', background: 'white', '--tw-ring-color': '#E2725B' }}
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-5 h-5 border-2 rounded-full animate-spin" style={{ borderColor: '#E2725B', borderTopColor: 'transparent' }} />
            <span className="ml-3 text-sm text-gray-400">Caricamento...</span>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users size={40} className="mb-4 opacity-20" />
            <p className="text-base font-medium text-gray-600 mb-2">Nessun contatto in rubrica</p>
            <p className="text-sm text-gray-400 mb-6 max-w-sm">
              La rubrica si popola automaticamente quando rispondi a una email.<br />
              Inizia a rispondere ai messaggi in arrivo!
            </p>
            <button
              onClick={() => navigate('/inbox')}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl"
              style={{ background: '#E2725B' }}
            >
              Vai alla posta in arrivo <ArrowRight size={14} />
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-sm">Nessun contatto trovato per "{search}"</p>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: '#D9D2C8' }}>
            {/* Header */}
            <div className="grid grid-cols-[2.5fr_2fr_1.5fr] gap-4 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-400" style={{ background: '#F5F0EA', borderBottom: '1px solid #D9D2C8' }}>
              <span>Contatto</span>
              <span>Dominio</span>
              <span>Categoria</span>
            </div>

            <div>
              {filtered.map((contact, i) => {
                const currentCatId = (contact.categories || [])[0] || null;
                const currentCat = currentCatId ? customCategories.find(c => c.id === currentCatId) : null;
                const isSaving = savingKey === contact.email;
                return (
                  <div
                    key={contact.email}
                    data-testid={`rubrica-row-${i}`}
                    className="grid grid-cols-[2.5fr_2fr_1.5fr] gap-4 px-5 py-3.5 items-center border-t transition-colors hover:bg-orange-50/40 group"
                    style={{ borderColor: '#EDE8E0', background: 'white' }}
                  >
                    <div
                      className="flex items-center gap-3 min-w-0 cursor-pointer"
                      onClick={() => openCompose({ to: contact.email, toName: contact.name })}
                      title={`Rispondi a ${contact.name || contact.email}`}
                    >
                      <Avatar name={contact.name} email={contact.email} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{contact.name || '(Sconosciuto)'}</div>
                        <div className="text-xs text-gray-400 truncate">{contact.email}</div>
                      </div>
                      <Mail size={13} className="text-gray-300 group-hover:text-orange-400 transition-colors ml-auto flex-shrink-0" />
                    </div>

                    <div className="flex items-center gap-1.5 min-w-0">
                      <Building2 size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600 truncate">{contact.domain || '—'}</span>
                    </div>

                    {/* Categoria editabile */}
                    <Popover
                      open={openPopover === contact.email}
                      onOpenChange={(o) => setOpenPopover(o ? contact.email : null)}
                    >
                      <PopoverTrigger asChild>
                        <button
                          data-testid={`rubrica-cat-btn-${i}`}
                          disabled={isSaving}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full font-medium transition-all hover:shadow-sm border w-fit"
                          style={
                            currentCat
                              ? { background: currentCat.bg_color, color: currentCat.color, borderColor: currentCat.color + '33' }
                              : { background: '#F1F3F4', color: '#9CA3AF', borderColor: '#E8EAED' }
                          }
                        >
                          {isSaving
                            ? <Loader2 size={11} className="animate-spin" />
                            : <Tag size={11} />}
                          {currentCat ? currentCat.name : 'Imposta'}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className="p-2 w-64"
                        data-testid={`rubrica-cat-popover-${i}`}
                      >
                        <CategoryPicker
                          contact={contact}
                          currentCatId={currentCatId}
                          customCategories={customCategories}
                          onSelect={(catId, applyToDomain) => handleSetCategory(contact, catId, applyToDomain)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-2.5 text-xs text-gray-400 text-right" style={{ background: '#F5F0EA', borderTop: '1px solid #D9D2C8' }}>
              {filtered.length} contatti{filtered.length !== contacts.length ? ` (di ${contacts.length})` : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
